import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LegalLayout = ({ title, updated, path, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-700 dark:text-gray-300">
      <Seo title={`${title} — Emboscale`} description={`Emboscale ${title.toLowerCase()}.`} path={path} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 text-sm">
          ← Back to emboscale.com
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-6 mb-2">{title}</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: {updated}</p>
        <div className="space-y-8 leading-relaxed [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 dark:[&_h2]:text-white [&_h2]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-emerald-600 dark:[&_a]:text-emerald-400">
          {children}
        </div>
      </div>
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" updated="October 3, 2026" path="/privacy">
    <section>
      <h2>Who we are</h2>
      <p>
        Emboscale ("we", "us") builds the Factory Agent, AI software that plans,
        simulates, and programs factories. For any privacy question or request,
        contact us at{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>.
      </p>
    </section>

    <section>
      <h2>What we collect</h2>
      <p>
        When you submit the demo request form, we collect the information you enter:
        your name, work email, company, message, and, if you choose to tell us, how
        you heard about us.
      </p>
      <p>
        To understand which pages are useful, we count visits with Umami, a
        privacy-friendly analytics service. It records aggregate statistics only:
        the page viewed, the referring website, browser, device type, and country,
        plus whether the "Book a demo" button was clicked or the demo form was sent.
        It sets no cookies, does not track you across websites, and does not store
        your IP address or any other information that identifies you. We do not use
        advertising trackers on this site.
      </p>
    </section>

    <section>
      <h2>How your data is processed</h2>
      <ul>
        <li>
          This site is hosted by Hostinger. When you visit any page, the web server
          processes your IP address in access logs, as every web server does — we use
          this solely to operate and secure the site (legitimate interest).
        </li>
        <li>
          Fonts and videos are served from our own hosting; no requests go to Google
          or other third-party content networks when you browse.
        </li>
        <li>
          Visit statistics are processed by Umami Software, Inc. (Umami Cloud, USA),
          with our analytics data hosted in the EU. Your IP address is used only to
          derive your country and an anonymous, regularly rotated visitor count; it
          is not stored. Legal basis: our legitimate interest in measuring the reach
          of our content (Art. 6(1)(f) GDPR). Because nothing is stored on or read
          from your device, no consent banner is required. You can block it with any
          content blocker without affecting the site.
        </li>
        <li>
          Form submissions are delivered to our inbox via Web3Forms, a form delivery
          service that processes your submission solely to forward it to us.
        </li>
        <li>We do not sell or share your data with anyone for marketing purposes.</li>
      </ul>
    </section>

    <section>
      <h2>Cookies and local storage</h2>
      <p>
        This site sets no cookies. If you switch between light and dark mode, that
        choice is saved in your browser's local storage so the site remembers it on
        your next visit. It stays on your device and is never sent to us.
      </p>
    </section>

    <section>
      <h2>Retention</h2>
      <p>
        We keep your contact details for as long as needed to respond to your request
        and conduct any follow-up conversation you have asked for, after which they
        are deleted.
      </p>
    </section>

    <section>
      <h2>Your rights</h2>
      <p>
        You can request access to, correction of, or deletion of the personal data
        you have sent us at any time by emailing{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>. If you are
        in the EU/EEA or UK, you additionally have the rights granted by the GDPR,
        including the right to lodge a complaint with your supervisory authority.
      </p>
    </section>

    <section>
      <h2>Changes</h2>
      <p>
        If we change this policy, we will update this page and the date above.
      </p>
    </section>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout title="Terms of Use" updated="July 22, 2026" path="/terms">
    <section>
      <h2>Acceptance</h2>
      <p>
        By using this website you agree to these terms. If you do not agree, please
        do not use the site.
      </p>
    </section>

    <section>
      <h2>Purpose of this site</h2>
      <p>
        This site provides information about Emboscale's products and lets you
        request a demo or technical overview. Product descriptions, timelines, and
        performance figures describe our goals and current capabilities and do not
        constitute a contractual commitment or an offer.
      </p>
    </section>

    <section>
      <h2>Intellectual property</h2>
      <p>
        The content of this site — text, graphics, videos, and branding — belongs to
        Emboscale and may not be reproduced or used commercially without our written
        permission.
      </p>
    </section>

    <section>
      <h2>Acceptable use</h2>
      <p>
        You agree not to misuse the site: no attempts to gain unauthorized access, no
        scraping at disruptive volume, no submitting unlawful or deceptive content
        through our forms.
      </p>
    </section>

    <section>
      <h2>No warranty</h2>
      <p>
        The site is provided "as is" without warranties of any kind. We do not
        guarantee that it will be uninterrupted or error-free.
      </p>
    </section>

    <section>
      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Emboscale is not liable for indirect
        or consequential damages arising from your use of this website. Nothing in
        these terms limits liability that cannot be limited by law.
      </p>
    </section>

    <section>
      <h2>Changes</h2>
      <p>
        We may update these terms; continued use of the site after an update
        constitutes acceptance. Questions:{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>.
      </p>
    </section>
  </LegalLayout>
);
